/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */

import React, { memo, type FC, useState, type SyntheticEvent } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { clientAppLogImageError } from '@/app/common/utils/clientAppLogImageError.ts';
import { getImageSize } from './getImageSize';
import { GalleryItemStyled, ShowMore } from './styled';
import { getGalleryImageSrc, getGalleryImageSrcSet } from '../../../../utils/images';
import { type HandleDealCardCtaCallBack } from '../../types';
import { ClickEventNameEnum } from '../../../../Curiosity/Analytics/utils/ClickEventNameEnum';

export type GalleryItemProps = {
  image: string;
  hotelId: number | undefined;
  imageIndex: number;
  viewMore?: string;
  isOverlay: boolean;
  isMobile: boolean;
  isNotClickable: boolean;
  isLast: boolean;
  isFirst: boolean;
  imagesCount: number;
  handleDealCardCtaCallBack: HandleDealCardCtaCallBack;
  handleImageClicked: (i: number) => void;
  fullWidthOnMobile?: boolean;
};

const GalleryItem: FC<GalleryItemProps> = memo(
  ({
    image,
    imageIndex,
    viewMore,
    isOverlay,
    isMobile,
    isNotClickable,
    isLast,
    isFirst,
    imagesCount,
    handleDealCardCtaCallBack,
    handleImageClicked,
    hotelId,
    fullWidthOnMobile,
  }) => {
    const { localization } = usePageWrapperData();
    const [hasError, setHasError] = useState(false);
    const { width, height } = getImageSize({ isOverlay, isMobile });
    const src = getGalleryImageSrc({ url: image, width, height });
    const srcSet = getGalleryImageSrcSet({ url: image, width, height });
    const isShowMore = isLast && !isNotClickable;
    const imgErrorLogsEnabled = localization?.configValuesApp?.imgErrorLogsEnabled;

    const handleShowMoreClick = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      e?.preventDefault();
      e?.stopPropagation();
      handleDealCardCtaCallBack({ e, payload: { event_name: ClickEventNameEnum.DiscoverMore } });
    };

    const handleImageError = async (e: SyntheticEvent<HTMLImageElement, Event> | ErrorEvent) => {
      if (e?.type === 'error') {
        setHasError(true);
        await clientAppLogImageError('GalleryItem:handleImageError', src, hotelId);
      }
    };

    const onClickImage = () => {
      handleImageClicked(imageIndex);
    };

    return (
      <GalleryItemStyled
        isOverlay={isOverlay}
        isLast={isLast}
        imagesCount={imagesCount}
        hotelId={isFirst ? hotelId : undefined}
        hasError={hasError}
        fullWidthOnMobile={fullWidthOnMobile}
        evolutionResultsOnlyEnabled={true}
      >
        {isShowMore && (
          <ShowMore
            aria-hidden="true"
            {...(isShowMore && {
              onClick: handleShowMoreClick,
            })}
          >
            {viewMore}
          </ShowMore>
        )}
        <img
          src={src}
          srcSet={srcSet}
          alt=""
          width={width}
          height={height}
          loading={isFirst ? 'eager' : 'lazy'}
          onClick={onClickImage}
          {...(imgErrorLogsEnabled ? { onError: handleImageError } : {})}
        />
      </GalleryItemStyled>
    );
  },
  (prevProps: GalleryItemProps, nextProps: GalleryItemProps) => {
    if (prevProps.isNotClickable !== nextProps.isNotClickable) return false;
    if (prevProps.isLast !== nextProps.isLast) return false;
    if (prevProps.isFirst !== nextProps.isFirst) return false;
    if (prevProps.image !== nextProps.image) return false;
    if (prevProps.hotelId !== nextProps.hotelId) return false;
    return true;
  }
);

GalleryItem.displayName = 'GalleryItem';

export default GalleryItem;
